import React, { useCallback } from 'react';
import {
  Box,
  Button,
  Col,
  Feedback,
  Row,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import { useDispatch, useSelector } from 'react-redux';

import { DEFAULT_QUERY_BUILDER_TAB_COLORS } from 'src/modules/query-builder/constants';
import {
  addApplicationTab,
  collapseRepository,
  removeAllItemsToCompare,
  removeItemToCompare,
  resetCompareError,
} from '../../../../actions';
import { APPS_MAP } from '../../../../constants/config';
import {
  StyledAudiencesToComparePanel,
  StyledButton,
  StyledButtonIcon,
  StyledSelectedItem,
} from './AudiencesToComparePanel.styles';

const AudiencesToComparePanel = () => {
  const dispatch = useDispatch();
  const audiencesToCompare = useSelector(
    state => state.repository.audiencesToCompare
  );
  const compareError = useSelector(state => state.repository.compareError);
  const getAudienceContent = useCallback(
    (audience, index) => {
      const color = DEFAULT_QUERY_BUILDER_TAB_COLORS[index].key;
      return (
        <StyledSelectedItem color={color}>
          <div
            style={{
              width: 'calc(100% - 20px)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Text
              size="xbody"
              weight="semi-bold"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {audience.label}
            </Text>
            <div style={{ position: 'absolute', right: '16px' }}>
              <Tooltip content="Remove" position="bottom" isInfoLabel>
                <StyledButtonIcon
                  color={color}
                  icon="remove"
                  size="small"
                  onClick={() => {
                    dispatch(removeItemToCompare(audience.id));
                  }}
                />
              </Tooltip>
            </div>
          </div>
        </StyledSelectedItem>
      );
    },
    [dispatch]
  );

  const handleCompareAudiences = useCallback(() => {
    dispatch(collapseRepository());
    dispatch(removeAllItemsToCompare());
    dispatch(addApplicationTab([...audiencesToCompare], APPS_MAP.COMPARE));
  }, [dispatch, audiencesToCompare]);

  return (
    <StyledAudiencesToComparePanel>
      <Box>
        <Row halign="center" valign="middle">
          <Col span="auto">
            <Button
              size="small"
              variant="green"
              disabled={audiencesToCompare.length < 2}
              onClick={handleCompareAudiences}
            >
              compare audiences
            </Button>
          </Col>
          <Col span="auto">
            <StyledButton
              size="small"
              id="btnCancel"
              variant="outlined"
              onClick={e => {
                e.stopPropagation();
                dispatch(removeAllItemsToCompare());
              }}
            >
              cancel
            </StyledButton>
          </Col>
        </Row>
      </Box>
      <Box pT="medium" style={{ minHeight: '100px' }}>
        {compareError ? (
          <Box pY="small" pX="small">
            <Feedback
              status="error"
              message="You can select up to 3 segments"
              closable
              onFeedbackClose={() => dispatch(resetCompareError())}
            />
          </Box>
        ) : (
          <Row halign="center" valign="middle">
            {audiencesToCompare.map((audience, index) => (
              <Col span={3} key={`audience-${index}`}>
                {getAudienceContent(audience, index)}
              </Col>
            ))}
            {audiencesToCompare.length < 3 && (
              <>
                {Array.from(Array(3 - audiencesToCompare.length)).map(
                  (_, index) => (
                    <Col span={3} key={`audience-empty-${index}`}>
                      <StyledSelectedItem color={'#c6c6c6'}>
                        <Text
                          size="xbody"
                          weight="semi-bold"
                          variant="dark_lighter"
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {`Audience ${audiencesToCompare.length +
                            (index + 1)} out of 3`}
                        </Text>
                      </StyledSelectedItem>
                    </Col>
                  )
                )}
              </>
            )}
          </Row>
        )}
      </Box>
    </StyledAudiencesToComparePanel>
  );
};

export default AudiencesToComparePanel;
